import i18n from '../../../../i18n';
import { Address } from '../../../../types/Common';

export const formatAddress = (address: Address): string => {
  if (address == null) return i18n.t('UNKNOWN') || 'Unknown';
  const { street, city, housenr, zipcode, boxnr, country_code } = address;
  if (
    street == null &&
    city == null &&
    housenr == null &&
    zipcode == null &&
    boxnr == null
  )
    return i18n.t('UNKNOWN') || 'Unknown';

  // Box number: In NL, the boxnr is not prefixed with the boxnr label
  const defaultBoxNr = `${i18n.t('AdresInput.manual.boxnr')?.toLowerCase()} ${
    boxnr || ''
  }`;
  const nlBoxNr = boxnr || '';
  const formattedBoxNr =
    country_code?.toUpperCase() === 'NL' ? nlBoxNr : defaultBoxNr;

  const addressString = `${street || ''} ${housenr || ''}${
    boxnr ? ` ${formattedBoxNr}` : ''
  }, ${zipcode || ''} ${city || ''}`;

  const trimmedAddress = addressString.replace(/\s,/g, ',');

  return trimmedAddress;
};

// Shamelessly stolen from https://stackoverflow.com/a/18650828
export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
export const sha256 = async (message: string): Promise<string> => {
  const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8); // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(''); // convert bytes to hex string
  return hashHex;
};

// https://dev.to/jorik/country-code-to-flag-emoji-a21
export const getFlagEmojiByCountryCode = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

export const mod = (n: number, m: number): number => {
  return ((n % m) + m) % m;
};

/**
 * Compare 2 values. If the value is `null`, `undefined`, `''` or `NaN` they are seen as equal.
 */
export const isEqualDefined = (a: unknown, b: unknown): boolean => {
  const _a = a == null || a === '' ? null : a;
  const _b = b == null || b === '' ? null : b;

  return JSON.stringify(_a) === JSON.stringify(_b);
};

export const removeNullishFromObject = <Type extends Record<string, unknown>>(
  obj: Type,
): Partial<Type> => {
  Object.keys({ ...obj }).map((key) =>
    obj[key] === undefined || obj[key] === null ? delete obj[key] : obj[key],
  );
  return obj;
};

export const makeUrlSafe = (string?: string) =>
  string
    ?.replace(/[^a-zA-Z0-9\s\-_]/g, '')
    .replace(/\s/g, '-')
    .toLowerCase();

/**
 * Convert a language code to a localized language name
 * @param languageCode - Language code (e.g., 'NL', 'FR', 'EN', 'DE')
 * @returns Localized language name or the original code if not found
 */
export const getLocalizedLanguageName = (languageCode: string): string => {
  const code = languageCode?.toUpperCase();

  switch (code) {
    case 'NL':
      return i18n.t('DUTCH') || 'Dutch';
    case 'FR':
      return i18n.t('FRENCH') || 'French';
    case 'EN':
      return i18n.t('ENGLISH') || 'English';
    case 'DE':
      return i18n.t('GERMAN') || 'German';
    default:
      return languageCode;
  }
};

/**
 * Convert a country code to a localized country name
 * @param countryCode - Country code (e.g., 'BE', 'NL', 'DE', 'FR')
 * @returns Localized country name or the original code if not found
 */
export const getLocalizedCountryName = (countryCode: string): string => {
  const code = countryCode?.toUpperCase();

  switch (code) {
    case 'BE':
      return i18n.t('Belgium') || 'Belgium';
    case 'NL':
      return i18n.t('The Netherlands') || 'The Netherlands';
    case 'DE':
      return 'Germany'; // Translation key not available, use English fallback
    case 'FR':
      return i18n.t('France') || 'France';
    default:
      return countryCode;
  }
};

export const formatPhoneNumberYellowhive = (
  phoneNumber?: string,
): string | undefined => {
  if (!phoneNumber) return undefined;

  // Remove + prefix and all spaces
  return phoneNumber.replace(/\+/g, '').replace(/\s/g, '');
};
